import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { plannerRequestConfig } from '../lib/plannerRequest';
import { usePoller, WAITLIST_POLL_INTERVAL_MS } from '../hooks/usePoller';
import {
  Calendar,
  Users,
  Inbox,
  Settings,
  Building2,
  UserCog,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Globe,
  Layers,
  Bell,
  Clock,
  Trash2,
  LayoutDashboard,
  Check,
  BarChart2,
  HelpCircle,
  MessageSquare,
  Radar,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { LegalModal } from './LegalModal';

const BrandIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect width="32" height="32" rx="6" fill="#7C3AED"/>
    <rect x="6" y="9" width="20" height="17" rx="2" stroke="white" strokeWidth="1.5" fill="none"/>
    <rect x="6" y="9" width="20" height="5" rx="1.5" fill="rgba(255,255,255,0.15)"/>
    <line x1="11" y1="7" x2="11" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="21" y1="7" x2="21" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="8" y="16" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="14" y="16" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="8" y="21" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="14" y="21" width="5" height="3" rx="1" fill="#A78BFA"/>
    <rect x="20" y="16" width="4" height="3" rx="1" fill="#4ADE80"/>
    <rect x="20" y="21" width="4" height="3" rx="1" fill="#4ADE80"/>
  </svg>
);

const PLATFORM_DOCUMENT_TYPES = new Set(['PLATFORM_PRIVACY', 'PLATFORM_APP_TERMS']);

const isUnconfiguredCenterDocument = (document) => (
  document?.documentType === 'CENTER_PRIVACY_TERMS' &&
  document.version === null &&
  document.contentHash === null &&
  document.accepted === false
);

const isValidClientLegalState = (state) => {
  if (!state || typeof state !== 'object' || typeof state.allCurrentAccepted !== 'boolean' || !Array.isArray(state.documents)) return false;
  if (state.documents.length < 2 || state.documents.length > 3) return false;

  const platformDocuments = state.documents.filter((document) => PLATFORM_DOCUMENT_TYPES.has(document?.documentType));
  const centerDocuments = state.documents.filter((document) => document?.documentType === 'CENTER_PRIVACY_TERMS');
  if (platformDocuments.length !== 2 || centerDocuments.length > 1) return false;
  if (new Set(platformDocuments.map((document) => document.documentType)).size !== 2) return false;

  const allDocumentsValid = state.documents.every((document) => {
    if (isUnconfiguredCenterDocument(document)) return true;
    return document && typeof document.documentType === 'string' &&
      typeof document.version === 'string' && typeof document.contentHash === 'string' &&
      typeof document.accepted === 'boolean' &&
      (PLATFORM_DOCUMENT_TYPES.has(document.documentType) || document.documentType === 'CENTER_PRIVACY_TERMS');
  });
  if (!allDocumentsValid) return false;

  const centerIsUnconfigured = centerDocuments.length === 1 && isUnconfiguredCenterDocument(centerDocuments[0]);
  const requiredDocumentsAccepted = platformDocuments.every((document) => document.accepted === true) &&
    centerDocuments.every((document) => centerIsUnconfigured || document.accepted === true);
  return state.allCurrentAccepted === requiredDocumentsAccepted ||
    (centerIsUnconfigured && requiredDocumentsAccepted && state.allCurrentAccepted === false);
};

const isClientLegalReleased = (state) => {
  if (!state || state.allCurrentAccepted !== false) return state?.allCurrentAccepted === true;
  const centerDocument = state.documents?.find((document) => document?.documentType === 'CENTER_PRIVACY_TERMS');
  if (centerDocument && !isUnconfiguredCenterDocument(centerDocument)) return false;
  return state.documents
    ?.filter((document) => PLATFORM_DOCUMENT_TYPES.has(document?.documentType))
    .every((document) => document.accepted === true) === true;
};

export const Layout = ({ children }) => {
  const { api, user, logout, isSuperAdmin, isCenterAdmin, language, setLanguage, impersonatingCenter, restoreSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [seenNotifIds, setSeenNotifIds] = useState(new Set());
  const [centerPlan, setCenterPlan] = useState(null);
  const [waUnreadTotal, setWaUnreadTotal] = useState(0);
  const [requestsPendingCount, setRequestsPendingCount] = useState(0);
  const [legalAccepted, setLegalAccepted] = useState(true);
  const [checkingLegal, setCheckingLegal] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(true);
  const [checkingDpa, setCheckingDpa] = useState(false);
  const [privacyAdamschedAccepted, setPrivacyAdamschedAccepted] = useState(true);
  const [checkingPrivacyAdamsched, setCheckingPrivacyAdamsched] = useState(false);
  const [privacyUsersAccepted, setPrivacyUsersAccepted] = useState(true);
  const [checkingPrivacyUsers, setCheckingPrivacyUsers] = useState(false);
  const [termsAppAccepted, setTermsAppAccepted] = useState(true);
  const [checkingTermsApp, setCheckingTermsApp] = useState(false);
  const [clientLegalState, setClientLegalState] = useState(null);
  const [clientCenterDocument, setClientCenterDocument] = useState(null);
  const [clientLegalError, setClientLegalError] = useState(false);
  const [clientLegalRetry, setClientLegalRetry] = useState(0);

  // GLOBAL WAITLIST OFFER MODAL
  const isClient = user?.role === 'CLIENT';
  const [waitlistItems, setWaitlistItems] = useState([]);
  const [dismissedOfferId, setDismissedOfferId] = useState(null);

  const fetchWaitlist = useCallback(async () => {
    if (!api || isClient) return;
    try {
      const res = await api.get('/waitlist', plannerRequestConfig());
      setWaitlistItems(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [api, isClient]);

  const offeredEntry = useMemo(() => {
    if (isClient) return null;
    const offered = (waitlistItems || []).find(e => e?.status === 'OFFERED');
    if (!offered) return null;
    if (offered.offerExpiresAt) {
      const exp = Date.parse(offered.offerExpiresAt);
      if (!Number.isNaN(exp) && exp < Date.now()) return null;
    }
    return offered;
  }, [waitlistItems, isClient]);

  const closeOfferedModal = useCallback(() => {
    if (offeredEntry?.id) setDismissedOfferId(offeredEntry.id);
  }, [offeredEntry?.id]);

  const confirmOfferedModal = useCallback(async () => {
    if (!offeredEntry?.id) return;
    try {
      await api.post(`/waitlist/${offeredEntry.id}/force-confirm`);
      setDismissedOfferId(offeredEntry.id);
      await fetchWaitlist();
      toast.success(t('success.layout.waitlistConfirmed'));
      try { navigate(0); } catch (e2) { window.location.reload(); }
    } catch (e) {
      // Frontend-specific key (NOT errors.http_409): the generic 409 fallback
      // says "reload" but the waitlist offer is already gone, so reloading
      // won't bring it back. Use the action-specific key instead (§7 round 2).
      toast.error(t('success.layout.waitlistConfirmTaken'));
    }
  }, [api, fetchWaitlist, offeredEntry?.id, navigate, t]);

  // Poller de waitlist sin overlap (D4/R3): self-scheduling + inFlightRef (≤1 en
  // vuelo), en vez del `setInterval` previo que solapaba bajo latencia (L1).
  // Solo este poller se migra; los de WhatsAppInbox/notificaciones son deuda
  // adyacente (D4).
  usePoller(fetchWaitlist, WAITLIST_POLL_INTERVAL_MS, !isClient);



  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      // cacheSecurityError is now active in AuthContext. /login
      // will render the blocking error page from that state. We
      // still navigate so the user sees the error UI immediately
      // and can hit "Reintentar limpieza".
      toast.error(
        err?.message ||
          t('success.layout.logoutError')
      );
      navigate('/login');
    }
  };

  const handleRestoreSuperAdmin = async () => {
    try {
      await restoreSuperAdmin();
      navigate('/centers');
    } catch (err) {
      // restoreSuperAdmin rejects on cache-purge failure; AuthContext
      // has already invalidated local credentials and activated
      // cacheSecurityError. Surface the error and route toward /login,
      // where the blocking page renders (mirror handleLogout).
      toast.error(
        err?.message ||
          t('success.layout.restoreSuperAdminError')
      );
      navigate('/login');
    }
  };

const fetchNotifications = useCallback(async () => {
  if (!api) return;
  try {
    const [countRes, listRes] = await Promise.all([
      api.get('/notifications/unread-count'),
      api.get('/notifications?unread_only=true'),
    ]);
    const count = countRes?.data?.count ?? 0;
    setUnreadCount(count);
    const unreadList = Array.isArray(listRes?.data) ? listRes.data : [];
    // Toast new notifications (best-effort)
    const newOnes = unreadList.filter(n => !seenNotifIds.has(n.id));
    if (newOnes.length > 0) {
      const next = new Set(seenNotifIds);
      newOnes.forEach(n => {
        next.add(n.id);
        toast(n.title || 'Notificación', { description: n.message || '' });
      });
      setSeenNotifIds(next);
    }
    // Keep a small list for panel (mix unread + recent)
    const recentRes = await api.get('/notifications');
    const recent = Array.isArray(recentRes?.data) ? recentRes.data : [];
    setNotifications(recent.slice(0, 20));
  } catch (e) {
    // silent
  }
}, [api, seenNotifIds]);

const deleteNotification = async (notifId) => {
  if (!api) return;
  try {
    await api.delete(`/notifications/${notifId}`);
    setNotifications((prev) => prev.filter((n) => n.id !== notifId));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  } catch (e) {
    toast.error(t('success.layout.notificationDeleteError'));
  }
};



  const navItems = [

    { path: '/', icon: Calendar, label: t('nav.planner'), key: 'planner', roles: ['CENTER_ADMIN', 'STAFF', 'CLIENT'] },
    { label: t('nav.myPacks'), path: "/client-packs", icon: Calendar, roles: ["CLIENT"] },

    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), roles: ['CENTER_ADMIN', 'SUPER_ADMIN'] },
    { path: '/clients', icon: Users, label: t('nav.clients'), key: 'clients', roles: ['CENTER_ADMIN', 'STAFF'] },
    { path: '/requests', icon: Inbox, label: t('nav.requests'), key: 'requests', roles: ['CENTER_ADMIN', 'STAFF'] },
    { path: '/waitlist', icon: Clock, label: t('nav.waitlist'), key: 'waitlist', roles: ['CENTER_ADMIN', 'STAFF', 'CLIENT'] },
    { path: '/services', icon: Layers, label: t('nav.services'), roles: ['CENTER_ADMIN'] },
    { path: '/staff', icon: UserCog, label: t('nav.staff'), roles: ['CENTER_ADMIN'] },
    { path: '/centers', icon: Building2, label: t('nav.centers'), roles: ['SUPER_ADMIN'] },
    { path: '/admin/costs', icon: BarChart2, label: t('nav.costs'), roles: ['SUPER_ADMIN'] },
    { path: '/internal/ai-lead-scanner', icon: Radar, label: t('aiLeadScanner.nav'), roles: ['SUPER_ADMIN'] },
    { path: '/internal/content-engine', icon: Sparkles, label: t('contentEngine.nav'), roles: ['SUPER_ADMIN'] },
    { path: '/whatsapp-inbox', icon: MessageSquare, label: t('nav.whatsappInbox'), key: 'whatsapp', roles: ['CENTER_ADMIN', 'STAFF'], requirePlan: ['EXPERT', 'PREMIUM'] },
    { path: '/audit-log', icon: ClipboardList, label: t('nav.auditLog'), roles: ['CENTER_ADMIN'] },
    { path: '/settings', icon: Settings, label: t('nav.settings'), roles: ['CENTER_ADMIN'] },
  ];

useEffect(() => {
  fetchNotifications();
  const id = setInterval(() => fetchNotifications(), 30000);
  return () => clearInterval(id);
}, [fetchNotifications]);

useEffect(() => {
  const isWaPlan = centerPlan === 'EXPERT' || centerPlan === 'PREMIUM';
  const isStaffOrAdmin = user?.role === 'CENTER_ADMIN' || user?.role === 'STAFF' || user?.role === 'SUPER_ADMIN';
  if (!api || !isWaPlan || !isStaffOrAdmin) return;
  const fetchWaUnread = async () => {
    try {
      const res = await api.get('/whatsapp/conversations', { params: { limit: 50 } });
      const convs = Array.isArray(res.data) ? res.data : [];
      setWaUnreadTotal(convs.reduce((sum, c) => sum + (c.unreadCount || 0), 0));
    } catch {}
  };
  fetchWaUnread();
  const id = setInterval(fetchWaUnread, 30000);
  return () => clearInterval(id);
}, [api, centerPlan, user?.role]);

// R1 / D4 — Poll /api/requests/pending-count every 30 s for roles that see
// the /requests menu (Layout.js:262 — CENTER_ADMIN + STAFF). The badge
// reads the source of truth (db.requests with centerId + status filter)
// on every cycle; no in-memory increment keyed by origin.
useEffect(() => {
  const isRequestsMenuRole = user?.role === 'CENTER_ADMIN' || user?.role === 'STAFF';
  if (!api || !isRequestsMenuRole) return;
  const fetchRequestsPending = async () => {
    try {
      const res = await api.get('/requests/pending-count');
      const n = Number(res?.data?.count);
      setRequestsPendingCount(Number.isFinite(n) ? n : 0);
    } catch {}
  };
  fetchRequestsPending();
  const id = setInterval(fetchRequestsPending, 30000);
  return () => clearInterval(id);
}, [api, user?.role]);



  useEffect(() => {
    if (!api || !user) {
      setLegalAccepted(true); setDpaAccepted(true);
      setPrivacyAdamschedAccepted(true);
      setPrivacyUsersAccepted(true); setTermsAppAccepted(true);
      return;
    }

    const checkAdminLegal = async () => {
      setCheckingLegal(true);
      try { setLegalAccepted(Boolean((await api.get('/legal/status'))?.data?.accepted)); }
      catch { setLegalAccepted(true); }
      finally { setCheckingLegal(false); }
    };

    const checkAdminDpa = async () => {
      setCheckingDpa(true);
      try { setDpaAccepted(Boolean((await api.get('/dpa/status'))?.data?.accepted)); }
      catch { setDpaAccepted(true); }
      finally { setCheckingDpa(false); }
    };

    const checkAdminPrivacy = async () => {
      setCheckingPrivacyAdamsched(true);
      try { setPrivacyAdamschedAccepted(Boolean((await api.get('/privacy-adamsched/status'))?.data?.accepted)); }
      catch { setPrivacyAdamschedAccepted(true); }
      finally { setCheckingPrivacyAdamsched(false); }
    };

    if (user.role === 'CENTER_ADMIN') {
      const runChecks = async () => {
        await checkAdminLegal();
        await checkAdminDpa();
        await checkAdminPrivacy();
      };
      runChecks();
    }

    if (user.role === 'CLIENT') {
      setClientLegalError(false);
      setClientLegalState(null);
      setClientCenterDocument(null);
      setCheckingPrivacyUsers(true);
      api.get('/client-legal/status')
        .then((res) => {
          if (!isValidClientLegalState(res?.data)) throw new Error('Malformed legal status');
          setClientLegalState(res.data);
        })
        .catch(() => setClientLegalError(true))
        .finally(() => setCheckingPrivacyUsers(false));
    }

    if (user.role === 'CENTER_ADMIN' || user.role === 'STAFF' || user.role === 'SUPER_ADMIN') {
      api.get('/my-subscription')
        .then((res) => setCenterPlan((res.data?.plan || 'BASIC').toUpperCase()))
        .catch(() => setCenterPlan('BASIC'));
    }
  }, [api, user, clientLegalRetry]);

  const clientPendingDocument = clientLegalState?.documents?.find((document) => (
    !document.accepted && !isUnconfiguredCenterDocument(document)
  ));
  const clientLegalReleased = isClientLegalReleased(clientLegalState);

  useEffect(() => {
    if (!isClient || !api || clientPendingDocument?.documentType !== 'CENTER_PRIVACY_TERMS') return;
    api.get('/center-legal/document')
      .then((res) => {
        const document = res?.data;
        if (!document || typeof document.version !== 'string' || typeof document.contentHash !== 'string' || typeof document.contentSnapshot !== 'string') {
          throw new Error('Malformed center legal document');
        }
        setClientCenterDocument(document);
      })
      .catch(() => setClientLegalError(true));
  }, [api, isClient, clientPendingDocument?.documentType, clientPendingDocument?.version, clientLegalRetry]);

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    if (user?.role === 'STAFF' && item.key) {
      const allowed = user.allowedSections;
      if (allowed && allowed.length > 0) return allowed.includes(item.key);
    }
    if (item.requirePlan && user?.role !== 'SUPER_ADMIN') {
      if (!item.requirePlan.includes(centerPlan)) return false;
    }
    return true;
  });
  return (
    <div className="min-h-screen bg-background flex">

      {/* CENTER_ADMIN — paso 1: Contrato SaaS v2 */}
      {!checkingLegal && user?.role === 'CENTER_ADMIN' && !legalAccepted && (
        <LegalModal
          title="Contrato de Prestación de Servicios SaaS"
          docUrl="/legal/saas_v2.md"
          showBillingCheckbox
          administrativeCopy
          onAccept={async () => {
            await api.post('/legal/accept', {});
            setLegalAccepted(true);
            toast.success(t('success.layout.legalAcceptedSaaS'));
          }}
          onLogout={handleLogout}
        />
      )}

      {/* CENTER_ADMIN — paso 2: DPA v2 */}
      {!checkingLegal && !checkingDpa && user?.role === 'CENTER_ADMIN' && legalAccepted && !dpaAccepted && (
        <LegalModal
          title="Anexo de Tratamiento de Datos (DPA)"
          docUrl="/legal/dpa_v2.md"
          showBillingCheckbox
          administrativeCopy
          onAccept={async () => {
            await api.post('/dpa/accept', {});
            setDpaAccepted(true);
            toast.success(t('success.layout.legalAcceptedDpa'));
          }}
          onLogout={handleLogout}
        />
      )}

      {/* CENTER_ADMIN — paso 3: Política de Privacidad AdamSched v1 */}
      {!checkingLegal && !checkingDpa && !checkingPrivacyAdamsched
        && user?.role === 'CENTER_ADMIN' && legalAccepted && dpaAccepted && !privacyAdamschedAccepted && (
        <LegalModal
          title="Política de Privacidad de AdamSched"
          docUrl="/legal/privacy_adamsched_v1.md"
          showBillingCheckbox
          administrativeCopy
          onAccept={async () => {
            await api.post('/privacy-adamsched/accept', {});
            setPrivacyAdamschedAccepted(true);
            toast.success(t('success.layout.legalAcceptedPrivacy'));
          }}
          onLogout={handleLogout}
        />
      )}

      {isClient && clientLegalError && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] px-4">
          <div role="alert" data-testid="client-legal-error" className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <h2 className="text-lg font-semibold">No se puede verificar el estado legal</h2>
            <p className="text-sm text-zinc-300 mt-2">El acceso permanece bloqueado. Reintenta o cierra sesión.</p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={handleLogout}>Cerrar sesión</Button>
              <Button onClick={() => setClientLegalRetry((value) => value + 1)}>Reintentar</Button>
            </div>
          </div>
        </div>
      )}

      {isClient && !clientLegalError && !checkingPrivacyUsers && clientLegalState && !clientLegalReleased && clientPendingDocument?.documentType === 'PLATFORM_PRIVACY' && (
        <LegalModal
          title="Política de Privacidad para Usuarios Finales"
          docUrl="/legal/privacy_users_v1.md"
          onAccept={async () => {
            try {
              await api.post('/privacy-users/accept', { version: clientPendingDocument.version, contentHash: clientPendingDocument.contentHash });
            } catch (error) {
              if (error?.response?.status === 409) setClientLegalRetry((value) => value + 1);
              throw error;
            }
            setClientLegalRetry((value) => value + 1);
            toast.success(t('success.layout.legalAcceptedPrivacy'));
          }}
          onLogout={handleLogout}
        />
      )}

      {isClient && !clientLegalError && !checkingPrivacyUsers && clientLegalState && !clientLegalReleased && clientPendingDocument?.documentType === 'PLATFORM_APP_TERMS' && (
        <LegalModal
          title="Términos de Uso de la App AdamSched"
          docUrl="/legal/terms_app_v1.md"
          onAccept={async () => {
            try {
              await api.post('/terms-app/accept', { version: clientPendingDocument.version, contentHash: clientPendingDocument.contentHash });
            } catch (error) {
              if (error?.response?.status === 409) setClientLegalRetry((value) => value + 1);
              throw error;
            }
            setClientLegalRetry((value) => value + 1);
            toast.success(t('success.layout.legalAcceptedTerms'));
          }}
          onLogout={handleLogout}
        />
      )}

      {isClient && !clientLegalError && !checkingPrivacyUsers && clientLegalState && !clientLegalReleased && clientPendingDocument?.documentType === 'CENTER_PRIVACY_TERMS' && clientCenterDocument && (
        <LegalModal
          title="Privacidad y normas del centro"
          documentContent={clientCenterDocument.contentSnapshot}
          downloadName="privacidad-y-normas-del-centro.md"
          onAccept={async () => {
            try {
              await api.post('/center-legal/accept', { version: clientCenterDocument.version, contentHash: clientCenterDocument.contentHash });
            } catch (error) {
              if (error?.response?.status === 409) setClientLegalRetry((value) => value + 1);
              throw error;
            }
            setClientLegalRetry((value) => value + 1);
          }}
          onLogout={handleLogout}
        />
      )}

      {offeredEntry && dismissedOfferId !== offeredEntry.id && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] px-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-[720px] max-h-[80vh] overflow-y-auto shadow-xl">
            <h2 className="text-lg font-semibold mb-3">
              {t('layout.waitlistOffer.title')}
            </h2>

            <p className="text-sm text-zinc-300 mb-4">
              {t('layout.waitlistOffer.body')}
              <br />
              <b>{t('layout.waitlistOffer.clientLabel')}:</b> {offeredEntry.clientName || '—'}<br />
              <b>{t('layout.waitlistOffer.serviceLabel')}:</b> {offeredEntry.calendarName || '—'}<br />
              <b>{t('layout.waitlistOffer.slotLabel')}:</b> {offeredEntry.desiredSlots?.[0] ? new Date(offeredEntry.desiredSlots[0]).toLocaleString() : '—'}
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeOfferedModal}>
                {t('common.close')}
              </Button>
              <Button onClick={confirmOfferedModal}>
                {t('layout.waitlistOffer.confirmBtn')}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-zinc-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-2">
              <BrandIcon size={32} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <span style={{ color: 'white', fontWeight: 500, fontSize: '20px' }}>AdamSched</span>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', letterSpacing: '3px', fontSize: '12px', marginTop: '2px' }}>Health & Fitness</span>
              </div>
            </Link>
            <button 
              className="md:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));

              const isWaInbox = item.path === '/whatsapp-inbox';
              const isRequests = item.path === '/requests';
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.path.replace('/', '') || 'planner'}`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {isWaInbox && waUnreadTotal > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 min-w-[18px] text-center leading-tight py-0.5">
                      {waUnreadTotal > 99 ? '99+' : waUnreadTotal}
                    </span>
                  )}
                  {isRequests && requestsPendingCount > 0 && (
                    <span
                      data-testid="nav-requests-badge"
                      className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 min-w-[18px] text-center leading-tight py-0.5"
                    >
                      {requestsPendingCount > 99 ? '99+' : requestsPendingCount}
                    </span>
                  )}
                  {isActive && !isWaInbox && !isRequests && <ChevronRight className="w-4 h-4 ml-auto" />}
                  {isActive && isWaInbox && waUnreadTotal === 0 && <ChevronRight className="w-4 h-4 ml-auto" />}
                  {isActive && isRequests && requestsPendingCount === 0 && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
            <a
              href="mailto:support@adamsched.com"
              className="sidebar-item"
              onClick={() => setSidebarOpen(false)}
            >
              <HelpCircle className="w-5 h-5" strokeWidth={1.5} />
              <span>{user?.role === 'CLIENT' ? 'Soporte' : 'Soporte técnico'}</span>
            </a>
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-zinc-800">
            <div className="relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { setNotifPanelOpen(!notifPanelOpen); if (!notifPanelOpen) fetchNotifications(); }}
          data-testid="notifications-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1 min-w-[18px] text-center">
              {unreadCount}
            </span>
          )}
        </Button>
        {notifPanelOpen && (
          <>
            {/* Fixed positioning to avoid being clipped by the sidebar/container overflow */}
            <div className="fixed left-4 bottom-20 w-96 max-w-[90vw] bg-card border border-zinc-800 rounded-sm shadow-lg z-[1000]">
              <div className="p-3 flex items-center justify-between border-b border-zinc-800">
                <div className="font-medium">{t('nav.notifications')}</div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => { await api.put('/notifications/read-all'); setUnreadCount(0); fetchNotifications(); }}
                  >
                    {t('layout.notifications.markAllRead')}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setNotifPanelOpen(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="max-h-96 overflow-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-sm text-zinc-500">{t('layout.notifications.empty')}</div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      tabIndex={0}
                      className={`p-3 border-b border-zinc-800 outline-none focus:ring-2 focus:ring-zinc-600 ${n.read ? 'opacity-70' : ''}`}
                    >
                      <div className="text-sm font-medium">{n.title}</div>
                      {n.message && <div className="text-sm text-zinc-400 mt-1">{n.message}</div>}
                      <div className="mt-2 flex gap-2 items-center">
                        {!n.read && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => { await api.put(`/notifications/${n.id}/read`); fetchNotifications(); }}
                          >
                            {t('layout.notifications.markRead')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Eliminar notificación"
                          onClick={() => deleteNotification(n.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')}>{t('layout.notifications.open')}</Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
           </>
        )}
      </div>

      <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-zinc-800/50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-medium">
                    {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium truncate">{user?.fullName}</div>
                    <div className="text-xs text-zinc-500 truncate">{user?.role?.replace('_', ' ')}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {[{ code: 'es', label: 'Español' }, { code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' }].map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}>
                    <Globe className="w-4 h-4 mr-2" />
                    {lang.label}
                    {language === lang.code && <Check className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Mobile header */}
        <header className="md:hidden h-14 flex items-center justify-between px-4 border-b border-zinc-800 bg-card">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-zinc-400 hover:text-white"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-display font-bold tracking-tight">AdamSched <span style={{ fontStyle: 'italic', fontWeight: 400, fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Health & Fitness</span></span>
          <div className="w-6" /> {/* Spacer */}
        </header>

        {/* Impersonation banner */}
        {impersonatingCenter && (
          <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between text-sm font-medium flex-shrink-0">
            <span>⚠️ Estás viendo AdamSched como admin de <strong>{impersonatingCenter}</strong></span>
            <button
              onClick={handleRestoreSuperAdmin}
              className="ml-4 bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-xs font-semibold"
            >
              Volver a SuperAdmin
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-hidden">
          {!isClient || (clientLegalReleased && !clientLegalError && !checkingPrivacyUsers)
            ? children
            : null}
        </main>
      </div>
    </div>
  );
};
